import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import api from "../lib/api";
import {
    Envelope, PaperPlaneTilt, PencilSimpleLine, CheckCircle, XCircle, ClockCounterClockwise,
    Coins, Lightning, CreditCard, Truck, ArrowsClockwise, Warning, CaretDown, CaretUp,
} from "@phosphor-icons/react";

const EVENT_META = {
    "case.created":               { icon: CheckCircle,     tone: "bg-emerald-50 text-emerald-700 border-emerald-200",   label: "Case created" },
    "case.updated":               { icon: PencilSimpleLine,tone: "bg-slate-50 text-slate-700 border-slate-200",         label: "Case updated" },
    "case.deleted":               { icon: XCircle,         tone: "bg-rose-50 text-rose-700 border-rose-200",            label: "Case deleted" },
    "ai_output.edited":           { icon: PencilSimpleLine,tone: "bg-amber-50 text-amber-700 border-amber-200",         label: "AI letter edited" },
    "ai_output.reverted":         { icon: ClockCounterClockwise, tone: "bg-slate-50 text-slate-700 border-slate-200",   label: "AI letter reverted" },
    "letter.checkout_initiated":  { icon: CreditCard,      tone: "bg-blue-50 text-blue-700 border-blue-200",            label: "Mailing checkout started" },
    "letter.mailed":              { icon: PaperPlaneTilt,  tone: "bg-emerald-50 text-emerald-700 border-emerald-200",   label: "Letter submitted to USPS" },
    "letter.in_transit":          { icon: Truck,           tone: "bg-blue-50 text-blue-700 border-blue-200",            label: "In transit" },
    "letter.delivered":           { icon: CheckCircle,     tone: "bg-emerald-50 text-emerald-700 border-emerald-200",   label: "Delivered" },
    "letter.returned":            { icon: Warning,         tone: "bg-rose-50 text-rose-700 border-rose-200",            label: "Returned to sender" },
    "offer.sent":                 { icon: Envelope,        tone: "bg-blue-50 text-blue-700 border-blue-200",            label: "Offer sent" },
    "offer.accepted":             { icon: CheckCircle,     tone: "bg-emerald-50 text-emerald-700 border-emerald-200",   label: "Offer accepted" },
    "offer.declined":             { icon: XCircle,         tone: "bg-rose-50 text-rose-700 border-rose-200",            label: "Offer declined" },
    "offer.countered":            { icon: ArrowsClockwise, tone: "bg-blue-50 text-blue-700 border-blue-200",            label: "Offer countered" },
    "offer.updated":              { icon: PencilSimpleLine,tone: "bg-slate-50 text-slate-700 border-slate-200",         label: "Offer edited" },
    "offer.deleted":              { icon: XCircle,         tone: "bg-rose-50 text-rose-700 border-rose-200",            label: "Offer deleted" },
    "auto_escalation.completed":  { icon: Lightning,       tone: "bg-emerald-50 text-emerald-700 border-emerald-200",   label: "Auto-escalation fired" },
    "auto_escalation.failed":     { icon: Warning,         tone: "bg-rose-50 text-rose-700 border-rose-200",            label: "Auto-escalation failed" },
    "credits.applied":            { icon: Coins,           tone: "bg-emerald-50 text-emerald-700 border-emerald-200",   label: "Credits added" },
    "autorecharge.charged":       { icon: Lightning,       tone: "bg-emerald-50 text-emerald-700 border-emerald-200",   label: "Auto-recharged" },
    "autorecharge.card_error":   { icon: Warning,         tone: "bg-rose-50 text-rose-700 border-rose-200",            label: "Auto-recharge failed" },
    "autorecharge.card_saved":    { icon: CreditCard,      tone: "bg-emerald-50 text-emerald-700 border-emerald-200",   label: "Card saved" },
};

const DEFAULT_META = { icon: ClockCounterClockwise, tone: "bg-slate-50 text-slate-600 border-slate-200", label: null };

function fmtDetails(action, details) {
    if (!details || typeof details !== "object") return null;
    const parts = [];
    if (action === "letter.mailed") {
        if (details.postage) parts.push(details.postage.replace(/_/g, " "));
        if (details.environment === "test") parts.push("sandbox");
    } else if (action === "ai_output.edited") {
        if (details.mode) parts.push(details.mode.replace(/_/g, " "));
        if (details.chars) parts.push(`${details.chars} chars`);
    } else if (action === "credits.applied" || action === "autorecharge.charged") {
        if (details.credit_cents) parts.push(`+$${(details.credit_cents / 100).toFixed(2)}`);
        if (details.pack_key) parts.push(details.pack_key);
    } else if (action?.startsWith("offer.")) {
        if (details.amount) parts.push(`$${Number(details.amount).toLocaleString()}`);
        if (details.recipient) parts.push(`→ ${details.recipient}`);
    }
    return parts.length ? parts.join(" · ") : null;
}

export default function CaseAuditTimeline({ caseId }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(true);

    useEffect(() => {
        if (!caseId) return;
        setLoading(true);
        api.get(`/cases/${caseId}/audit`)
            .then((r) => setEvents(r.data || []))
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, [caseId]);

    const shown = collapsed ? events.slice(0, 5) : events;

    return (
        <Card className="p-6 border-slate-900" data-testid="case-audit-timeline">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-semibold text-slate-900">Case timeline</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Every letter mailed, offer sent, edit and payment on this case.
                    </p>
                </div>
                <span className="text-xs text-slate-500 font-mono">{events.length} events</span>
            </div>

            {loading ? (
                <p className="text-sm text-slate-500 mt-4">Loading timeline…</p>
            ) : events.length === 0 ? (
                <p className="text-sm text-slate-500 mt-4">No events yet.</p>
            ) : (
                <>
                    <ol className="mt-5 space-y-3">
                        {shown.map((e) => {
                            const meta = EVENT_META[e.action] || DEFAULT_META;
                            const Icon = meta.icon;
                            const label = meta.label || e.action.replace(/[._]/g, " ");
                            const details = fmtDetails(e.action, e.details);
                            return (
                                <li key={e.id} data-testid={`audit-event-${e.id}`} className="flex items-start gap-3">
                                    <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full border shrink-0 ${meta.tone}`}>
                                        <Icon size={14} weight="fill" />
                                    </span>
                                    <div className="flex-1 min-w-0 pt-1">
                                        <p className="text-sm text-slate-900">{label}</p>
                                        {details && <p className="text-xs text-slate-600 mt-0.5">{details}</p>}
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            {new Date(e.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                                            {e.actor === "system" && <span className="ml-2 text-slate-500">· system</span>}
                                        </p>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                    {events.length > 5 && (
                        <div className="mt-4 text-center">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCollapsed((c) => !c)}
                                data-testid="audit-toggle"
                                className="text-slate-600"
                            >
                                {collapsed ? <><CaretDown size={14} className="mr-1.5" /> Show all {events.length}</> : <><CaretUp size={14} className="mr-1.5" /> Show less</>}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}
